import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useServiceCategories from "../../hook/useGarage";

const isShowingInBooking = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const AllServiceCategoriesLayer = ({ onCategoriesChanged }) => {

  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryNameAr, setNewCategoryNameAr] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [newCategoryDescriptionAr, setNewCategoryDescriptionAr] = useState("");

  const [newCategoryShowingInBooking, setNewCategoryShowingInBooking] =
    useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingNameAr, setEditingNameAr] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  const [editingDescriptionAr, setEditingDescriptionAr] = useState("");
  const [editingShowingInBooking, setEditingShowingInBooking] = useState(false);
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [update, setUpdate] = useState(0);
  const [deletingCategory, setDeletingCategory] = useState(null);
  // undefined = counting, null = could not count, number = services in the category
  const [deleteServiceCount, setDeleteServiceCount] = useState(undefined);
  const [isDeleting, setIsDeleting] = useState(false);

  const token = localStorage.getItem("token");
  const { categories, loading } = useServiceCategories(token, update);
  const navigate = useNavigate();

  // Count every service in the category when the delete dialog opens
  useEffect(() => {
    if (!deletingCategory) return;
    let cancelled = false;
    const fetchServiceCount = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/services?category_id=${deletingCategory.id}&page=0&limit=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json();
        if (cancelled) return;
        if (response.ok) {
          const pagination = json.data.pagination || {};
          setDeleteServiceCount(
            pagination.total_count ?? pagination.total ?? 0,
          );
        } else {
          setDeleteServiceCount(null);
        }
      } catch (error) {
        console.error("Error fetching service count:", error);
        if (!cancelled) setDeleteServiceCount(null);
      }
    };
    fetchServiceCount();
    return () => {
      cancelled = true;
    };
  }, [deletingCategory, token]);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) {
      toast.error("Category name is required");
      return;
    }

    const formData = new FormData();
    formData.append("name", newCategoryName.trim());
    formData.append("name_ar", newCategoryNameAr.trim());
    formData.append("description", newCategoryDescription.trim());
    formData.append("description_ar", newCategoryDescriptionAr.trim());
    formData.append(
      "is_showing_in_booking",
      String(newCategoryShowingInBooking),
    );

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/admin/service-categories`,
        {
          method: "POST",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const json = await response.json();
      if (response.ok) {
        toast.success("Category added successfully");
        setNewCategoryName("");
        setNewCategoryNameAr("");
        setNewCategoryDescription("");
        setNewCategoryDescriptionAr("");
        setNewCategoryShowingInBooking(false);
        setIsCategoryFormOpen(false);
        setUpdate((u) => u + 1);
        onCategoriesChanged?.();
      } else {
        toast.error(json.errorMessage || "Failed to add category");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while adding the category");
    }
  };

  const startEditing = (category) => {
    setEditingId(category.id);
    setEditingName(category.name);
    setEditingNameAr(category.name_ar || "");
    setEditingDescription(category.description || "");
    setEditingDescriptionAr(category.description_ar || "");
    setEditingShowingInBooking(
      isShowingInBooking(category.is_showing_in_booking),
    );
    setIsCategoryFormOpen(true);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
    setEditingNameAr("");
    setEditingDescription("");
    setEditingDescriptionAr("");
    setEditingShowingInBooking(false);
    setIsCategoryFormOpen(false);
  };

  const saveEditing = async (categoryId) => {
    if (!editingName.trim()) {
      toast.error("Category name is required");
      return;
    }

    const formData = new FormData();
    formData.append("name", editingName.trim());
    formData.append("name_ar", editingNameAr.trim());
    formData.append("description", editingDescription.trim());
    formData.append("description_ar", editingDescriptionAr.trim());
    formData.append("is_showing_in_booking", String(editingShowingInBooking));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/admin/service-categories/${categoryId}`,
        {
          method: "PUT",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const json = await response.json();
      if (response.ok) {
        toast.success("Category updated successfully");
        cancelEditing();
        setUpdate((u) => u + 1);
        onCategoriesChanged?.();
      } else {
        toast.error(json.errorMessage || "Failed to update category");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while updating the category");
    }
  };

  const handleDelete = (category) => {
    setDeleteServiceCount(undefined);
    setDeletingCategory(category);
  };

  const closeDeleteDialog = () => {
    if (!isDeleting) setDeletingCategory(null);
  };

  const confirmDelete = async () => {
    if (!deletingCategory) return;
    setIsDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/admin/service-categories/${deletingCategory.id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.ok) {
        toast.success("Category deleted");
        setDeletingCategory(null);
        setUpdate((u) => u + 1);
        onCategoriesChanged?.();
      } else {
        const json = await response.json().catch(() => ({}));
        toast.error(json.errorMessage || "Failed to delete category");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while deleting the category");
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteSubcategoryCount = (deletingCategory?.subcategories || []).length;
  const deleteServicesText =
    deleteServiceCount === undefined
      ? "Counting services..."
      : deleteServiceCount === null
        ? "All services under this category"
        : `${deleteServiceCount} service${deleteServiceCount === 1 ? "" : "s"}`;

  return (
    <div className="card h-100 p-0 radius-12 mb-24">
      <div className="card-header border-bottom bg-base py-16 px-24">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="text-md mb-0">Service Categories</h6>
          <div className="d-flex align-items-center gap-2">
            {/* <button
              type="button"
              className="btn btn-sm btn-outline-primary-600"
              onClick={() => navigate("/service-category/all")}
            >
              View All
            </button> */}
            <button
              type="button"
              className="btn btn-sm btn-primary-600"
              onClick={() => {
                setEditingId(null);
                setNewCategoryName("");
                setNewCategoryNameAr("");
                setNewCategoryDescription("");
                setNewCategoryShowingInBooking(false);
                setIsCategoryFormOpen(true);
              }}
            >
              <i className="ri-add-line" /> Add Category
            </button>
          </div>
        </div>
      </div>
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                {/* <th scope="col">ID</th> */}
                <th scope="col">
                  Name <span className="text-xs text-secondary">(en)</span>
                </th>
                <th scope="col">
                  Name <span className="text-xs text-secondary">(ar)</span>
                </th>
                <th scope="col">
                  Description{" "}
                  <span className="text-xs text-secondary">(en)</span>
                </th>
                <th scope="col">
                  Description{" "}
                  <span className="text-xs text-secondary">(ar)</span>
                </th>
                <th scope="col">Subcategories</th>
                <th scope="col">Show in Booking</th>
                <th scope="col" className="text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No categories found
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr
                    key={category.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/service-category/${category.id}`)}
                  >
                    {/* <td>{category.id}</td> */}
                    <td>
                      {" "}
                      <div>{category.name || "-"}</div>
                    </td>
                    <td>
                      {category.name_ar ? (
                        <div
                          dir="rtl"
                          style={{ textAlign: "right", marginTop: "4px" }}
                        >
                          {category.name_ar}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <div
                        className="text-truncate"
                        style={{ maxWidth: "250px" }}
                        title={category.description || "-"}
                      >
                        {category.description || "-"}
                      </div>
                    </td>
                    <td>
                      {category.description_ar ? (
                        <div
                          dir="rtl"
                          className="text-truncate"
                          style={{
                            textAlign: "right",
                            marginTop: "4px",
                            maxWidth: "250px",
                          }}
                          title={category.description_ar}
                        >
                          {category.description_ar}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{(category.subcategories || []).length}</td>
                    <td>
                      {isShowingInBooking(category.is_showing_in_booking)
                        ? "Yes"
                        : "No"}
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/service-category/${category.id}`);
                          }}
                          className="bg-info-focus bg-hover-primary-200 text-primary-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="View subcategories and services"
                        >
                          <Icon icon="lucide:eye" className="menu-icon" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(category);
                          }}
                          className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Edit"
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(category);
                          }}
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Delete"
                        >
                          <Icon
                            icon="fluent:delete-24-regular"
                            className="menu-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {isCategoryFormOpen && (
        <>
          <div className="modal-backdrop fade show" onClick={cancelEditing} />
          <div
            className="modal d-block"
            role="dialog"
            aria-modal="true"
            aria-labelledby="category-form-title"
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "700px", width: "95%" }}
            >
              <div className="modal-content">
                <form
                  onSubmit={
                    editingId
                      ? (e) => {
                          e.preventDefault();
                          saveEditing(editingId);
                        }
                      : handleAddCategory
                  }
                >
                  <div className="modal-header">
                    <h5 className="fs-5 modal-title" id="category-form-title">
                      {editingId
                        ? "Edit Service Category"
                        : "Add Service Category"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={cancelEditing}
                      aria-label="Close"
                    />
                  </div>
                  <div className="modal-body">
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="category-name" className="form-label">
                          Name <span className="text-danger">*</span>
                        </label>
                        <input
                          id="category-name"
                          type="text"
                          placeholder="Enter category name"
                          className="form-control"
                          value={editingId ? editingName : newCategoryName}
                          onChange={(e) =>
                            editingId
                              ? setEditingName(e.target.value)
                              : setNewCategoryName(e.target.value)
                          }
                        />
                      </div>

                      <div className="col-md-6">
                        <label
                          htmlFor="category-name-ar"
                          className="form-label"
                          style={{
                            textAlign: "right",
                            direction: "rtl",
                            display: "block",
                            width: "100%",
                          }}
                        >
                          الاسم
                        </label>
                        <input
                          id="category-name-ar"
                          type="text"
                          placeholder="أدخل اسم الفئة"
                          className="form-control"
                          dir="rtl"
                          value={editingId ? editingNameAr : newCategoryNameAr}
                          onChange={(e) =>
                            editingId
                              ? setEditingNameAr(e.target.value)
                              : setNewCategoryNameAr(e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label
                        htmlFor="category-description"
                        className="form-label"
                      >
                        Description
                      </label>
                      <textarea
                        id="category-description"
                        className="form-control"
                        placeholder="Enter description"
                        rows="3"
                        value={
                          editingId
                            ? editingDescription
                            : newCategoryDescription
                        }
                        onChange={(e) =>
                          editingId
                            ? setEditingDescription(e.target.value)
                            : setNewCategoryDescription(e.target.value)
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label
                        htmlFor="category-description-ar"
                        className="form-label"
                        style={{
                          textAlign: "right",
                          direction: "rtl",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        الوصف
                      </label>
                      <textarea
                        id="category-description-ar"
                        className="form-control"
                        placeholder="أدخل الوصف"
                        rows="3"
                        dir="rtl"
                        value={
                          editingId
                            ? editingDescriptionAr
                            : newCategoryDescriptionAr
                        }
                        onChange={(e) =>
                          editingId
                            ? setEditingDescriptionAr(e.target.value)
                            : setNewCategoryDescriptionAr(e.target.value)
                        }
                      />
                    </div>
                    <div className="form-check d-flex align-items-center gap-2">
                      <input
                        id="category-showing-in-booking"
                        type="checkbox"
                        className="form-check-input m-0"
                        checked={
                          editingId
                            ? editingShowingInBooking
                            : newCategoryShowingInBooking
                        }
                        onChange={(e) =>
                          editingId
                            ? setEditingShowingInBooking(e.target.checked)
                            : setNewCategoryShowingInBooking(e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label m-0"
                        htmlFor="category-showing-in-booking"
                      >
                        Show in Booking
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary-600">
                      {editingId ? "Save Changes" : "Add Category"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {deletingCategory && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeDeleteDialog}
          />
          <div
            className="modal d-block"
            role="dialog"
            aria-modal="true"
            aria-labelledby="category-delete-title"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="fs-5 modal-title" id="category-delete-title">
                    Delete Category
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeDeleteDialog}
                    disabled={isDeleting}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-12">
                    Delete <strong>{deletingCategory.name}</strong>?
                  </p>
                  <div className="alert alert-danger mb-0" role="alert">
                    <p className="fw-semibold mb-8">
                      This permanently deletes:
                    </p>
                    <ul className="mb-8" style={{ paddingLeft: 20 }}>
                      <li>
                        <strong>{deleteServicesText}</strong> under this
                        category
                      </li>
                      <li>
                        <strong>
                          {deleteSubcategoryCount} subcategor
                          {deleteSubcategoryCount === 1 ? "y" : "ies"}
                        </strong>
                      </li>
                      <li>These services' links to garages</li>
                    </ul>
                    <p className="mb-0 fw-semibold">This cannot be undone.</p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={closeDeleteDialog}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger-600"
                    onClick={confirmDelete}
                    disabled={isDeleting || deleteServiceCount === undefined}
                  >
                    {isDeleting ? "Deleting..." : "Delete Category"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllServiceCategoriesLayer;
